import { useRef, useEffect } from 'react'
import { QRCodeSVG } from 'qrcode.react'
import html2canvas from 'html2canvas'

// ======================== Types ========================

export interface InvoiceItem {
    serial: number
    product: string
    code?: string
    hsCode?: string
    saleType: string
    uom: string
    quantity: number
    unitPrice: number
    discount: number
    saleExTax: number
    tax: number
    subtotal: number
}

export interface InvoiceData {
    invoiceNumber: string
    date: string
    poNumber?: string
    deliveryNumber?: string
    docNumber?: string
    sellerName: string
    sellerAddress: string
    sellerCity: string
    sellerNtn: string
    sellerGst?: string
    sellerPhone?: string
    buyerName: string
    buyerAddress?: string
    buyerCity?: string
    buyerNtn?: string
    buyerStn?: string
    buyerCnic?: string
    buyerPhone?: string
    items: InvoiceItem[]
    subtotal: number
    taxAmount: number
    discountAmount: number
    totalAmount: number
    taxRate: number
    fbrInvoiceNumber?: string
    usid?: string
    posId?: string
    fbrStatus?: 'pending' | 'submitted' | 'accepted' | 'rejected'
    [key: string]: any // allow dynamic legacy properties like FBR_Invoice_Number or Invoice_Id
}

// ======================== Component ========================

interface FBRInvoiceProps {
    data: InvoiceData
    onPrint?: () => void
    onClose?: () => void
    onInvoiceCaptured?: (blob: Blob) => void
    onApprove?: () => void
    isSubmitting?: boolean
}

export function FBRInvoice({ data, onPrint, onClose, onInvoiceCaptured, onApprove, isSubmitting }: FBRInvoiceProps) {
    const printRef = useRef<HTMLDivElement>(null)

    const handlePrint = () => {
        if (printRef.current) {
            const printContents = printRef.current.innerHTML
            const printWindow = window.open('', '_blank')
            if (printWindow) {
                printWindow.document.write(`<!DOCTYPE html><html><head>
<title>Invoice ${data.invoiceNumber}</title>
<style>
  @page { margin: 10mm; size: A4 portrait; }
  body { font-family: Arial, Helvetica, sans-serif; font-size: 11px; color: black; margin: 0; padding: 0; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  * { box-sizing: border-box; }
  .invoice-table { width: 100%; border-collapse: collapse; margin-top: 15px; border-top: 2px solid black; border-bottom: 2px solid black; }
  .invoice-table th, .invoice-table td { border: 1px solid black; padding: 6px; }
  .totals-section { display: flex; justify-content: flex-end; margin-top: 20px; font-size: 12px; }
  .totals-table { width: 300px; }
  .totals-row { display: flex; justify-content: space-between; margin-bottom: 5px; }
  .grand-total { display: flex; justify-content: space-between; font-weight: bold; font-size: 14px; border-top: 1px solid black; border-bottom: 1px solid black; padding: 5px 0; margin-top: 5px; }
  @media print {
      .no-print { display: none !important; }
  }
</style>
</head><body>${printContents}</body></html>`)
                printWindow.document.close()
                printWindow.focus()
                setTimeout(() => { printWindow.print(); printWindow.close() }, 250)
            }
        }
        onPrint?.()
    }

    // Auto-capture the invoice as an image for cloud backup
    useEffect(() => {
        if (!onInvoiceCaptured || !printRef.current) return;

        const capture = async () => {
            try {
                // Wait briefly to ensure images/fonts are loaded
                await new Promise(resolve => setTimeout(resolve, 500));
                if (!printRef.current) return;

                const canvas = await html2canvas(printRef.current, {
                    scale: 2, // High resolution
                    useCORS: true,
                    logging: false,
                    backgroundColor: '#ffffff'
                });

                canvas.toBlob((blob) => {
                    if (blob) onInvoiceCaptured(blob);
                }, 'image/png', 1.0);
            } catch (err) {
                console.error("Failed to capture invoice:", err);
            }
        };

        capture();
    }, [data.invoiceNumber]); // Re-capture if invoice number changes

    const f = (n: number) => n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

    // Safe date formatting: handle ISO strings, locale strings, or fallback to raw
    const formattedDate = (() => {
        try {
            if (!data.date) return '';
            const trimmed = String(data.date).trim();
            // Direct match YYYY-MM-DD to avoid UTC day-shift
            const ymd = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})/);
            if (ymd) {
                return `${ymd[3]}/${ymd[2]}/${ymd[1]}`;
            }
            // Direct match DD/MM/YYYY or DD-MM-YYYY
            const dmy = trimmed.match(/^(\d{1,2})[\/-](\d{1,2})[\/-](\d{4})/);
            if (dmy) {
                return `${dmy[1].padStart(2, '0')}/${dmy[2].padStart(2, '0')}/${dmy[3]}`;
            }
            // Try ISO date
            const isoDate = new Date(trimmed);
            if (!isNaN(isoDate.getTime())) {
                const dd = String(isoDate.getDate()).padStart(2, '0');
                const mm = String(isoDate.getMonth() + 1).padStart(2, '0');
                const yyyy = isoDate.getFullYear();
                return `${dd}/${mm}/${yyyy}`;
            }
            return trimmed;
        } catch {
            return data.date;
        }
    })();

    const fbrInvNumber = data.fbrInvoiceNumber || data.Invoice_Id || data.FBR_Invoice_Number; // Removed usid, as it's the internal POS invoice number
    const qrData = fbrInvNumber || data.usid || data.invoiceNumber || 'N/A'; // Still fallback to local ID for the QR code if no FBR number yet
    
    // Determine if this is an FBR invoice based on the presence of any FBR-specific field
    const isFBR = Boolean(data.fbrInvoiceNumber || data.Invoice_Id || data.FBR_Invoice_Number) || Boolean(data.usid) || Boolean(data.posId) || Boolean(data.fbrStatus);

    return (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center sm:p-4">
            <div className="bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl w-full sm:max-w-4xl max-h-[95vh] sm:max-h-[95vh] overflow-auto flex flex-col">
                {/* Toolbar (Not printed) */}
                <div className="sticky top-0 z-10 bg-white border-b px-6 py-3 flex items-center justify-between rounded-t-2xl no-print">
                    <div className="flex items-center gap-3">
                        <span className="text-lg">🧾</span>
                        <span className="font-bold text-slate-900">Invoice View</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <button onClick={handlePrint}
                            className="bg-blue-600 hover:bg-blue-700 text-white px-5 py-2 rounded-xl text-sm font-bold shadow-md transition-all flex items-center gap-2">
                            🖨️ Print Invoice
                        </button>
                        {onClose && (
                            <button onClick={onClose} className="text-slate-400 hover:text-slate-600 p-2 rounded-lg hover:bg-slate-100 transition-all">✕</button>
                        )}
                    </div>
                </div>

                {/* ==================== PRINTABLE INVOICE ==================== */}
                <div className="p-8 bg-[#e5e5e5] flex-1 overflow-auto flex justify-center">
                    {/* Simulated A4 Paper */}
                    <div ref={printRef} className="bg-white shadow-sm" style={{ width: '210mm', minHeight: '297mm', padding: '20mm 15mm', fontFamily: 'Arial, sans-serif', color: 'black' }}>

                        {/* Header: Seller Info */}
                        <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                            <h1 style={{ fontSize: '24px', fontWeight: 'bold', margin: '0 0 5px 0', textTransform: 'uppercase', letterSpacing: '0.03em' }}>
                                {data.sellerName || 'DYNAMIC INDUSTRIAL SOLUTIONS'}
                            </h1>
                            <div style={{ fontSize: '11px', lineHeight: '1.4' }}>
                                {data.sellerAddress || 'SUIT NO. 411 BLOCK-F1, MAIN BOULEVARD PAK ARAB SOCIETY, 18-KM FEROZEPUR ROAD'}
                                {data.sellerCity ? `, ${data.sellerCity}` : ''}
                                {data.sellerProvince ? `, ${data.sellerProvince}` : ''}
                                <br />
                                NTN : {data.sellerNtn || '4113099'}<br />
                                STN : {data.sellerGst || data.sellerStn || '0300411300919'}
                                {data.sellerPhone ? <><br />Phone : {data.sellerPhone}</> : null}
                                {data.sellerEmail ? <> | Email : {data.sellerEmail}</> : null}
                            </div>
                        </div>

                        {/* FBR Logo & QR */}
                        {isFBR && (
                            <div style={{ textAlign: 'center', marginBottom: '25px' }}>
                                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '20px', marginBottom: '8px' }}>
                                    <img src="/fbr-logo.png" alt="FBR Digital Invoicing System" style={{ height: '55px', objectFit: 'contain' }} />
                                    <QRCodeSVG value={qrData} size={60} level="M" />
                                </div>
                                <div style={{ fontSize: '12px', fontWeight: 'bold' }}>
                                    FBR Invoice #: {fbrInvNumber || 'PENDING SUBMISSION'}
                                </div>
                            </div>
                        )}

                        {/* Middle Section: Buyer & Meta */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '15px' }}>
                            <div>
                                <h2 style={{ fontSize: '20px', fontWeight: 'normal', margin: '0 0 10px 0' }}>Invoice</h2>
                                <div style={{ fontSize: '11px', lineHeight: '1.4' }}>
                                    <strong>{data.buyerName}</strong><br />
                                    {data.buyerAddress && <>{data.buyerAddress}<br /></>}
                                    {data.buyerCity && <>{data.buyerCity}<br /></>}
                                    PUNJAB<br />
                                    Pakistan<br />
                                    {data.buyerNtn && <>NTN {data.buyerNtn}<br /></>}
                                    {data.buyerCnic && <>CNIC {data.buyerCnic}<br /></>}
                                    <>STN {data.buyerStn || 'N/A'}</>
                                </div>
                            </div>
                            <div style={{ fontSize: '11px', lineHeight: '1.5', fontWeight: 'bold', paddingTop: '30px', textAlign: 'right' }}>
                                <div>Date {formattedDate}</div>
                                {data.poNumber && <div>Ref No. {data.poNumber}</div>}
                                {data.deliveryNumber && <div>DC No. {data.deliveryNumber}</div>}
                                {data.docNumber && <div>Doc No. {data.docNumber}</div>}
                            </div>
                        </div>

                        {/* Items Table */}
                        <table style={{ width: '100%', borderCollapse: 'collapse', borderTop: '2px solid black', borderBottom: '2px solid black', fontSize: '11px' }}>
                            <thead>
                                <tr>
                                    <th style={{ border: '1px solid black', padding: '6px', textAlign: 'left', width: '40px' }}>SrNo</th>
                                    <th style={{ border: '1px solid black', padding: '6px', textAlign: 'left', width: '60px' }}>Code</th>
                                    <th style={{ border: '1px solid black', padding: '6px', textAlign: 'left' }}>Details</th>
                                    <th style={{ border: '1px solid black', padding: '6px', textAlign: 'left', width: '60px' }}>UM Unit</th>
                                    <th style={{ border: '1px solid black', padding: '6px', textAlign: 'right', width: '80px' }}>UM Quantity</th>
                                    <th style={{ border: '1px solid black', padding: '6px', textAlign: 'right', width: '70px' }}>UM Rate</th>
                                    <th style={{ border: '1px solid black', padding: '6px', textAlign: 'right', width: '90px' }}>Amount</th>
                                    <th style={{ border: '1px solid black', padding: '6px', textAlign: 'right', width: '80px' }}>Hs Code</th>
                                </tr>
                            </thead>
                            <tbody>
                                {(() => {
                                    const hsCounters: Record<string, number> = {};
                                    return data.items.map((item: any, idx) => {
                                        // Fallbacks for older invoice structures where keys might differ
                                        const uom = item.uom || item.unit || 'pcs';
                                        const qty = item.quantity || 1;
                                        const rate = item.unitPrice ?? item.price ?? 0;
                                        const exTax = item.saleExTax ?? (qty * rate);
                                        const hsCodeStr = item.hsCode || '—';

                                        // Use the code from the cart/order if available, otherwise auto-generate
                                        let displayCode = item.code;
                                        if (!displayCode) {
                                            hsCounters[hsCodeStr] = (hsCounters[hsCodeStr] || 0) + 1;
                                            displayCode = hsCounters[hsCodeStr].toString().padStart(4, '0');
                                        }

                                        return (
                                            <tr key={idx}>
                                                <td style={{ border: '1px solid black', padding: '6px', textAlign: 'left' }}>{item.serial || (idx + 1)}</td>
                                                <td style={{ border: '1px solid black', padding: '6px', textAlign: 'left' }}>{displayCode}</td>
                                            <td style={{ border: '1px solid black', padding: '6px', textAlign: 'left' }}>{item.product || item.name}</td>
                                            <td style={{ border: '1px solid black', padding: '6px', textAlign: 'left' }}>{uom.toLowerCase()}</td>
                                            <td style={{ border: '1px solid black', padding: '6px', textAlign: 'right' }}>{qty.toFixed(4)}</td>
                                            <td style={{ border: '1px solid black', padding: '6px', textAlign: 'right' }}>{Math.round(rate)}</td>
                                            <td style={{ border: '1px solid black', padding: '6px', textAlign: 'right' }}>{f(exTax)}</td>
                                            <td style={{ border: '1px solid black', padding: '6px', textAlign: 'right' }}>{item.hsCode || '—'}</td>
                                        </tr>
                                        );
                                    });
                                })()}
                            </tbody>
                        </table>

                        {/* Totals Section */}
                        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '20px', fontSize: '12px' }}>
                            <div style={{ width: '300px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
                                    <span>Total Items:</span>
                                    <span>{data.items.reduce((acc, item) => acc + item.quantity, 0)}</span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
                                    <span>Sub Total:</span>
                                    <span>{f(data.subtotal)}</span>
                                </div>
                                {data.discountAmount > 0 && (
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
                                        <span>Discount:</span>
                                        <span>-{f(data.discountAmount)}</span>
                                    </div>
                                )}
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
                                    <span>GST:</span>
                                    <span>{f(data.taxAmount || 0)}</span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 'bold', fontSize: '14px', borderTop: '1px solid black', borderBottom: '1px solid black', padding: '5px 0', marginTop: '5px' }}>
                                    <span>Total:</span>
                                    <span>Rs. {f(data.totalAmount)}</span>
                                </div>
                            </div>
                        </div>

                        {/* Footer Section */}
                        <div style={{ textAlign: 'center', marginTop: '40px', paddingTop: '10px', fontSize: '11px', borderTop: '1px solid black' }}>
                            This is a system generated invoice and does not require any signatures.
                        </div>

                    </div>
                </div>

                {/* FBR Approval Bar */}
                {onApprove && data.fbrStatus === 'pending' && (
                    <div style={{ margin: '15px 0', padding: '12px 16px', backgroundColor: '#fefce8', border: '1px solid #facc15', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div>
                            <div style={{ fontSize: '13px', fontWeight: 'bold', color: '#854d0e' }}>⏳ FBR Submission Pending</div>
                            <div style={{ fontSize: '11px', color: '#a16207', marginTop: '2px' }}>Review this invoice, then click Submit to send it to FBR.</div>
                        </div>
                        <button onClick={onApprove} disabled={isSubmitting}
                            style={{ padding: '8px 20px', backgroundColor: isSubmitting ? '#9ca3af' : '#16a34a', color: 'white', border: 'none', borderRadius: '8px', fontSize: '12px', fontWeight: 'bold', cursor: isSubmitting ? 'not-allowed' : 'pointer' }}>
                            {isSubmitting ? 'Submitting...' : '✅ Submit to FBR'}
                        </button>
                    </div>
                )}

                {/* FBR Success Bar */}
                {data.fbrStatus === 'accepted' && (
                    <div style={{ margin: '15px 0', padding: '12px 16px', backgroundColor: '#f0fdf4', border: '1px solid #22c55e', borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <span style={{ fontSize: '20px' }}>✅</span>
                        <div>
                            <div style={{ fontSize: '13px', fontWeight: 'bold', color: '#166534' }}>FBR Invoice Submitted Successfully</div>
                            <div style={{ fontSize: '11px', color: '#15803d', marginTop: '2px' }}>FBR Invoice #: {data.fbrInvoiceNumber || 'Confirmed'}</div>
                        </div>
                    </div>
                )}

                {/* Action Buttons */}
                <div style={{ display: 'flex', justifyContent: 'center', gap: '10px', marginTop: '10px' }} className="no-print">
                    <button onClick={handlePrint} style={{ padding: '8px 20px', backgroundColor: '#2563eb', color: 'white', border: 'none', borderRadius: '8px', fontSize: '12px', fontWeight: 'bold', cursor: 'pointer' }}>
                        🖨️ Print Invoice
                    </button>
                    {onClose && (
                        <button onClick={onClose} style={{ padding: '8px 20px', backgroundColor: '#64748b', color: 'white', border: 'none', borderRadius: '8px', fontSize: '12px', fontWeight: 'bold', cursor: 'pointer' }}>
                            ✕ Close
                        </button>
                    )}
                </div>
            </div>
        </div>
    )
}
